import { motion } from 'framer-motion';

/**
 * EngagedlyTraits, a designed replacement for the static "personality
 * traits" image that used to sit inside Wendy's persona in the Engagedly
 * Homepage Redesign case study. Each trait is shown as a labelled slider
 * with Wendy's position marked on it, a live, richer signal than a
 * static PNG.
 */

const easeOut = [0.2, 0.8, 0.2, 1];

// Percent is on the 0–100 scale, where 0 = left extreme, 100 = right extreme.
const traits = [
    {
        key: 'social',
        leftLabel: 'Introvert',
        rightLabel: 'Extrovert',
        note: 'Energises in conversation, meeting-heavy days feel good.',
        percent: 82,
    },
    {
        key: 'team',
        leftLabel: 'Team-player',
        rightLabel: 'Solo-player',
        note: 'Ships solo but happy to sync when the work needs it.',
        percent: 72,
    },
    {
        key: 'structure',
        leftLabel: 'Chaotic',
        rightLabel: 'Organised',
        note: 'Time-blocks her day; treats her calendar like production code.',
        percent: 88,
    },
    {
        key: 'learn',
        leftLabel: 'Slow learner',
        rightLabel: 'Fast learner',
        note: 'Picks up tools within a week; teaches teammates by month two.',
        percent: 90,
    },
];

const TraitRow = ({ trait, index }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-4%' }}
        transition={{ duration: 0.5, delay: index * 0.06, ease: easeOut }}
        data-testid={`engagedly-trait-${trait.key}`}
        className="flex flex-col gap-2 md:gap-2.5"
    >
        <div className="flex items-center justify-between gap-4 font-mono-brand text-[10.5px] uppercase tracking-[0.22em] text-[var(--ink-3)]">
            <span>{trait.leftLabel}</span>
            <span>{trait.rightLabel}</span>
        </div>

        {/* Slider track */}
        <div className="relative h-2.5 rounded-full bg-[var(--surface-2)] border border-[var(--line)] overflow-visible">
            {/* Fill from left up to the marker */}
            <motion.div
                initial={{ width: '0%' }}
                whileInView={{ width: `${trait.percent}%` }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: index * 0.06 + 0.2, ease: easeOut }}
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[var(--accent)]/40 to-[var(--accent)]"
            />
            {/* Marker dot */}
            <motion.span
                aria-hidden
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                    type: 'spring',
                    stiffness: 260,
                    damping: 18,
                    delay: index * 0.06 + 0.6,
                }}
                className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-[var(--accent)] border-2 border-[var(--bg)] shadow-[0_0_0_3px_var(--accent-glow)]"
                style={{ left: `${trait.percent}%` }}
            />
        </div>

        {trait.note && (
            <p className="font-sans-brand text-[12.5px] leading-[1.5] text-[var(--ink-2)]">
                {trait.note}
            </p>
        )}
    </motion.div>
);

export const EngagedlyTraits = () => (
    <motion.figure
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-6%' }}
        transition={{ duration: 0.55, ease: easeOut }}
        data-testid="engagedly-traits"
        className="mb-6 md:mb-8 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5 md:p-7"
    >
        <header className="mb-5 md:mb-6 flex items-start justify-between gap-4">
            <div>
                <div className="font-mono-brand text-[10.5px] uppercase tracking-[0.28em] text-[var(--ink-3)]">
                    Personality traits
                </div>
                <h4 className="mt-2 font-display text-2xl md:text-3xl text-[var(--ink)]">
                    Where Wendy sits
                    <span className="text-[var(--accent)]">.</span>
                </h4>
            </div>
            <span
                className="hidden md:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-[var(--line-2)] font-mono-brand text-[10px] uppercase tracking-[0.18em] text-[var(--ink-2)]"
            >
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
                {traits.length} traits mapped
            </span>
        </header>

        <div className="flex flex-col gap-6 md:gap-7">
            {traits.map((t, i) => (
                <TraitRow key={t.key} trait={t} index={i} />
            ))}
        </div>
    </motion.figure>
);

export default EngagedlyTraits;
